import { BKJComboBoxAsync, ErrorBoundary } from '@bookjane2/bookjane-design-library';
import {
  LocationFilterDataContext,
  LocationFilterDataProvider,
  useLocationFilterDataContext,
} from 'components/LocationFilterInput/LocationFilterInput.context';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { usePathname } from 'hooks/usePathname';
import { useSelector } from 'react-redux';
import { isUserAgencyAndAgencyLocationFilterForcedDisabledSelector } from 'store/selectors/featureFlagSelectors';
import { useTranslation } from 'hooks/useTranslation';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { LocationFilterWrapper } from 'components/LocationFilterInput/LocationFilterInput.styled';
import { useCallback, useEffect, useRef, useState, FC } from 'react';
import { LOCATION_INPUT_DEBOUNCE_MS } from 'components/LocationFilterInput/LocationFilterInput.constants';

function LocationFilterInputComponent(
  props: Omit<ILocationFilterProps, 'options'> & { skipFetchOnMount?: boolean },
) {
  const { value, onChange, name = 'location', disabled, dropDownWidth, skipFetchOnMount } = props;
  const { t } = useTranslation();
  const pathname = usePathname();

  const {
    data: options = [],
    values,
    onChange: onSearchChange,
    onMount,
  } = useLocationFilterDataContext();

  const hasFetchedRef = useRef(!skipFetchOnMount);

  const handleDropdownOpen = useCallback(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      onMount();
    }
  }, [onMount]);

  const debounceTimerRef = useRef<number | null>(null);
  const [displayValue, setDisplayValue] = useState(values.byName);

  useEffect(() => {
    setDisplayValue(values.byName);
  }, [values.byName]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const newValue = e.target.value;
      setDisplayValue(newValue);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        onSearchChange(e);
      }, LOCATION_INPUT_DEBOUNCE_MS);
    },
    [onSearchChange],
  );

  const isLocationFilterForcedDisabled = useSelector(
    isUserAgencyAndAgencyLocationFilterForcedDisabledSelector,
  );
  let __disabled__ = disabled;

  const isTheCurrentPageAnythingOtherThanTimesheets =
    pathname !== '/timesheets' && !pathname.includes('/communications');

  if (isLocationFilterForcedDisabled && isTheCurrentPageAnythingOtherThanTimesheets) {
    __disabled__ = true;
  }
  return (
    <LocationFilterWrapper width={dropDownWidth} onFocus={handleDropdownOpen}>
      <BKJComboBoxAsync
        name={name}
        type="Single"
        value={value}
        icon="Location1"
        searchKey="byName"
        disabled={__disabled__}
        onChange={onChange}
        action="OpenToggle"
        options={options}
        searchValue={displayValue}
        onSearchChange={handleSearchChange}
        context={LocationFilterDataContext}
        placeholder={t('schedule:SEARCH_LOCATION_PLACEHOLDER')}
      />
    </LocationFilterWrapper>
  );
}

export const LocationFilterInput: FC<
  Omit<ILocationFilterProps, 'options'> & { skipFetchOnMount?: boolean }
> = ({ skipFetchOnMount, ...props }) => {
  return (
    <ErrorBoundary
      componentName="LocationFilterInput"
      showLogo={!WhiteLabelAccessor.isWhiteLabelled()}
    >
      <LocationFilterDataProvider skipFetchOnMount={skipFetchOnMount}>
        <LocationFilterInputComponent {...props} skipFetchOnMount={skipFetchOnMount} />
      </LocationFilterDataProvider>
    </ErrorBoundary>
  );
};
